'use client'

import { Fragment, useEffect, useRef, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'
import Avatar from 'boring-avatars'
import { AlertTriangle, Search, ShoppingBag, Heart, ChevronDown, ChevronRight, ArrowRight, BadgePercent, Check, Gift, LayoutGrid, LoaderCircle, Medal, PackageX, Plus, ShieldAlert, Trash2, Truck, User, Menu, X } from 'lucide-react'
import { Dialog, DialogPanel, Disclosure, DisclosureButton, DisclosurePanel, Transition, TransitionChild } from '@headlessui/react'
import { useCart, useSearch, useNavigation } from '@/lib/commerce/hooks'
import { formatImageURL, formatPrice, getCheckoutPath, isCustomerAuthActive, getProductDisplayPrice, getBuyXTriggerCount, getBuyXGetYRewardRule } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import Input from '@/components/elements/Input'
import { Button } from '@/components/elements/Button'
import CartItemSidebar from '../cart/CartItemSidebar'
import SubHeader from './SubHeader'

function MobileDrawer({ open, onClose, links, sections, pathname, showLogin, vendor }: { open: boolean, onClose: () => void, links: any[], sections: any[], pathname: string, showLogin: boolean, vendor?: any }) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-40 lg:hidden font-inter" onClose={onClose}>
        <TransitionChild as={Fragment} enter="transition-opacity ease-linear duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity ease-linear duration-300" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/25" />
        </TransitionChild>
        <div className="fixed inset-0 z-40 flex">
          <TransitionChild as={Fragment} enter="transition ease-in-out duration-300 transform" enterFrom="-translate-x-full" enterTo="translate-x-0" leave="transition ease-in-out duration-300 transform" leaveFrom="translate-x-0" leaveTo="-translate-x-full">
            <DialogPanel className="relative flex w-[88%] max-w-sm flex-col bg-cream shadow-2xl rounded-r-3xl overflow-hidden">
              <div className="h-14 px-5 flex items-center justify-between border-b border-foreground/5 shrink-0">
                <a href={navigateUrl('/', pathname)} onClick={onClose} className="flex items-center gap-2">
                  {vendor?.logo ? (
                    <img src={formatImageURL(vendor.logo)} alt={vendor?.name} className="h-6 max-w-32 w-auto object-contain" />
                  ) : (
                    <span className="text-[20px] font-bold font-gilroy text-foreground tracking-[-0.02em]">{vendor?.name || 'Shop'}</span>
                  )}
                </a>
                <button onClick={onClose} aria-label="Хаах" className="w-9 h-9 inline-flex items-center justify-center rounded-full text-foreground/60 hover:text-foreground hover:bg-foreground/5 transition-colors">
                  <X className="size-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto overscroll-contain">
                <ul className="px-2 py-3">
                  {links.map((item: any) => (
                    <li key={item.name}>
                      <a
                        href={navigateUrl(item.href, pathname)}
                        onClick={onClose}
                        className={`flex items-center justify-between rounded-xl px-3 h-12 text-[15px] font-medium transition-colors ${item.isSale ? 'text-red-600 hover:bg-red-50' : 'text-foreground hover:bg-foreground/5'}`}
                      >
                        <span>{item.name}</span>
                        <ChevronRight className="size-4 opacity-40" />
                      </a>
                    </li>
                  ))}
                </ul>
                {sections.length > 0 && (
                  <div className="border-t border-foreground/5 px-3 py-4">
                    <div className="text-[11px] tracking-[0.2em] uppercase font-semibold text-foreground/55 px-2 mb-2">Ангилал</div>
                    {sections.map((s: any) => (
                      <Disclosure key={s.id} as="div" className="border-b border-foreground/5 last:border-0">
                        {({ open: catOpen }) => (
                          <>
                            <DisclosureButton className="w-full flex items-center justify-between rounded-xl px-3 h-12 text-[15px] font-medium text-foreground hover:bg-foreground/5 transition-colors">
                              <span>{s.name}</span>
                              <ChevronDown className={`size-4 opacity-50 transition-transform ${catOpen ? 'rotate-180' : ''}`} />
                            </DisclosureButton>
                            <DisclosurePanel as="ul" className="pl-3 pb-2 space-y-1">
                              <li>
                                <a href={s.href} onClick={onClose} className="block rounded-lg px-3 py-2 text-[14px] text-foreground/70 hover:text-foreground hover:bg-foreground/5 transition-colors">
                                  Бүх {s.name.toLowerCase()}
                                </a>
                              </li>
                              {(s.items || []).map((child: any, i: number) => (
                                <li key={i}>
                                  <a href={child.href} onClick={onClose} className="block rounded-lg px-3 py-2 text-[14px] text-foreground/70 hover:text-foreground hover:bg-foreground/5 transition-colors">
                                    {child.name}
                                  </a>
                                </li>
                              ))}
                            </DisclosurePanel>
                          </>
                        )}
                      </Disclosure>
                    ))}
                  </div>
                )}
              </div>
              {showLogin && (
                <div className="border-t border-foreground/5 p-4 shrink-0">
                  <a
                    href={navigateUrl('/login', pathname)}
                    onClick={onClose}
                    className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full bg-foreground text-white text-[15px] font-semibold hover:bg-foreground/90 transition-colors"
                  >
                    <User className="size-4" />Нэвтрэх
                  </a>
                </div>
              )}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}

const FEATURED_TINTS: { bg: string, fg: string }[] = [
  { bg: '#E8E2FF', fg: '#1a1130' },
  { bg: '#FFD9E7', fg: '#7B1027' },
  { bg: '#FFE0C9', fg: '#9A3412' },
  { bg: '#DCEEF6', fg: '#0B3D5C' },
  { bg: '#CFE9D7', fg: '#0F3D2B' },
  { bg: '#FEF08A', fg: '#854D0E' },
]

function MegaMenu({ open, onClose, sections, highlights, pathname }: { open: boolean, onClose: () => void, sections: any[], highlights: any[], pathname: string }) {
  if (!open) return null
  const featured = sections.slice(0, 6)
  return (
    <div
      className="absolute left-0 right-0 top-full bg-cream border-t border-foreground/10 shadow-[0_24px_48px_-20px_rgba(15,8,40,0.15)] z-40"
      onMouseLeave={onClose}
    >
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10 py-10 grid grid-cols-12 gap-10">
        <div className="col-span-3">
          {highlights?.length > 0 && (
            <div className="mb-4">
              <ul>
                {highlights.map((h: any) => (
                  <li key={h.name}>
                    <a
                      href={navigateUrl(h.href, pathname)}
                      className={`group flex items-center justify-between py-3 transition-colors ${h.isSale ? 'text-red-600 hover:text-red-700' : 'text-foreground/85 hover:text-foreground'}`}
                    >
                      <span className="text-[15px] font-semibold font-gilroy tracking-[-0.005em]">{h.name}</span>
                      <ArrowRight className={`size-3.5 transition-transform group-hover:translate-x-0.5 ${h.isSale ? 'opacity-80' : 'opacity-45 group-hover:opacity-80'}`} />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="text-[18px] font-bold font-gilroy mb-5">
            Онцлох ангилал
          </div>
          <ul className="space-y-3.5">
            {featured.map((s: any, i: number) => {
              const tint = FEATURED_TINTS[i % FEATURED_TINTS.length]
              return (
                <li key={s.id || i}>
                  <a href={s.href || '#'} className="flex items-center gap-3 text-[14px] text-foreground/80 hover:text-foreground transition-colors">
                    <span style={{ background: tint.bg, color: tint.fg }} className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[11px] font-bold font-gilroy shrink-0">
                      {(s.name || '?').charAt(0)}
                    </span>
                    <span>{s.name}</span>
                  </a>
                </li>
              )
            })}
          </ul>
          <a href={navigateUrl('/product', pathname)} className="mt-5 inline-flex items-center gap-1.5 text-[14px] text-foreground hover:underline">
            Бүх ангилал <ArrowRight className="size-3.5" />
          </a>
        </div>
        <div className="col-span-9">
          <div className="text-[18px] font-bold font-gilroy mb-5">
            Бүтээгдэхүүн ба ангилал
          </div>
          <div className="grid grid-cols-3 gap-x-8 gap-y-8">
            {sections.map((s: any) => (
              <div key={s.id} className="min-w-0">
                <a href={s.href || '#'} className="block text-[14px] font-semibold text-foreground hover:underline mb-3 truncate">
                  {s.name}
                </a>
                {(s.items || []).length > 0 && (
                  <ul className="space-y-2">
                    {(s.items || []).slice(0, 6).map((c: any, i: number) => (
                      <li key={i}>
                        <a href={c.href || '#'} className="text-[13.5px] text-foreground/65 hover:text-foreground transition-colors truncate block">
                          {c.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function SearchPopover({ open, onClose, pathname, isAuthed }: { open: boolean, onClose: () => void, pathname: string, isAuthed: boolean }) {
  const { query, setQuery, results, categories: searchCategories, loading, history, runSearch, clearHistory } = useSearch()
  if (!open) return null
  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} aria-hidden="true" />
      <div className="absolute left-0 right-0 top-full bg-cream border-t border-foreground/10 shadow-[0_24px_48px_-20px_rgba(15,8,40,0.15)] z-40">
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 py-6">
          <Input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { runSearch(); onClose() } }}
            placeholder="Бүтээгдэхүүн хайх..."
            leftIcon={Search}
            rightIcon={X}
            rightAction={onClose}
            inputSize="lg"
          />
          {query ? (
            <div className="mt-5">
              {loading ? (
                <div className="text-[13px] text-foreground/50">Хайж байна...</div>
              ) : results.length === 0 && searchCategories.length === 0 ? (
                <div className="text-[13px] text-foreground/50">Илэрц олдсонгүй</div>
              ) : (
                <>
                  {searchCategories.length > 0 && (
                    <div className="mb-4">
                      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-foreground/45 mb-2">Ангилал</div>
                      <div className="flex flex-wrap gap-2">
                        {searchCategories.map((cat: any) => (
                          <a
                            key={cat.id}
                            href={navigateUrl(`/product?category=${cat.slug || cat.id}`, pathname)}
                            onClick={onClose}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-foreground/5 hover:bg-foreground/10 text-[13.5px] text-foreground transition-colors"
                          >
                            <LayoutGrid className="h-3.5 w-3.5 text-foreground/55 shrink-0" />
                            <span className="truncate max-w-40">{cat.name}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {results.length > 0 && (
                    <>
                      <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-foreground/45 mb-2">Илэрц</div>
                      <ul className="divide-y divide-foreground/10 border-y border-foreground/10">
                        {results.slice(0, 6).map((product: any) => {
                          const variant = product?.variant
                          const imageUrl = variant?.images?.length > 0 ? formatImageURL(variant.images[0]) : product?.images?.[0] ? formatImageURL(product.images[0]) : formatImageURL('placeholder.png')
                          const price = getProductDisplayPrice(product, variant, isAuthed)
                          return (
                            <li key={product.id}>
                              <a
                                href={navigateUrl(`/product/${product.customId || product.id}`, pathname)}
                                onClick={onClose}
                                className="flex items-center gap-3 py-2.5 px-1 -mx-1 rounded-lg hover:bg-foreground/5 transition-colors"
                              >
                                <img src={imageUrl} alt={product?.name} className="h-12 w-12 rounded-lg object-cover bg-white shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-[14px] font-medium text-foreground truncate">{product?.name}</div>
                                  {variant?.name && <div className="text-[12px] text-foreground/55 truncate">{variant.name}</div>}
                                </div>
                                <div className="text-[14px] font-semibold text-foreground tabular-nums shrink-0">{formatPrice(price)}</div>
                              </a>
                            </li>
                          )
                        })}
                      </ul>
                      <button
                        type="button"
                        onClick={() => { runSearch(); onClose() }}
                        className="w-full mt-3 px-3 py-2.5 text-[13.5px] font-medium text-foreground/65 hover:text-foreground hover:bg-foreground/5 rounded-lg text-center transition-colors cursor-pointer"
                      >
                        "{query}" гэсэн бүх илэрцийг үзэх
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          ) : history.length > 0 ? (
            <div className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-foreground/45">Сүүлд хайсан</div>
                <Button variant="ghost" size="sm" onClick={clearHistory}>Цэвэрлэх</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {history.map(h => (
                  <Button key={h} variant="ghost" size="sm" className="hover:bg-[#F1EEE7]!" onClick={() => { setQuery(h); runSearch(h) }}>{h}</Button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default function Header({ vendor, categories, subscription, campaigns, mobileMenuOpen, setMobileMenuOpen }: { vendor?: any, categories?: any, subscription?: any, campaigns?: any, mobileMenuOpen: boolean, setMobileMenuOpen: (open: boolean) => void }) {
  const { cartCount, validateCart, isValidating } = useCart()
  const { navigation } = useNavigation({ vendor, subscription, campaigns, categories, icons: { loyalty: Medal } })
  const { data: session } = useSession()
  const pathname = usePathname() || ''
  const router = useRouter()
  const [megaOpen, setMegaOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [cartOpen, setCartOpen] = useState(false)
  const [activeId, setActiveId] = useState('discover')

  const showLogin = isCustomerAuthActive(vendor)
  const isAuthed = !!session?.user?.id
  const canPlaceOrder = vendor?.canPlaceOrder !== false

  const openCart = async () => {
    setCartOpen(true)
    if (cartCount > 0 && !isValidating) {
      try { await validateCart() } catch (e) { console.error('Error validating cart:', e) }
    }
  }

  // Bar is position:fixed so macOS rubber-band overscroll can't drag it (sticky rides the bounce).
  // Slide it up via `top` (clamped >=0, capped at the SubHeader height) so only the SubHeader scrolls
  // away and the nav stays pinned. The fixed bar reserves no space, so an invisible normal-flow GHOST
  // of the bar (below) holds its EXACT height on the SSR first paint — no measurement, no flash.
  const subHeaderRef = useRef<HTMLDivElement>(null)
  const subHeaderHeightRef = useRef(0)
  const [barShift, setBarShift] = useState(0)
  useEffect(() => {
    const el = subHeaderRef.current
    if (!el) return
    const update = () => { subHeaderHeightRef.current = el.offsetHeight }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  useEffect(() => {
    let raf = 0
    const onScroll = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        const y = Math.max(0, window.scrollY) // clamp overscroll -> bar never rides the bounce
        setBarShift(Math.min(y, subHeaderHeightRef.current))
      })
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  return (
    <>
      <div className="fixed inset-x-0 z-30" style={{ top: -barShift }}>
        <div ref={subHeaderRef}>
          <SubHeader vendor={vendor} />
        </div>
        <header className="w-full bg-cream">
          <div className="relative border-b border-foreground/5 font-inter">
            <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 h-16 lg:h-19 flex items-center gap-3 lg:gap-8">
              <button onClick={() => setMobileMenuOpen(true)} aria-label="Цэс" className="lg:hidden -ml-1 w-10 h-10 inline-flex items-center justify-center rounded-full text-foreground hover:bg-foreground/5 transition-colors">
                <Menu className="size-5" />
              </button>
              <a href={navigateUrl('/', pathname)} className="shrink-0">
                {vendor?.logo ? (
                  <img src={formatImageURL(vendor.logo)} alt={vendor?.name} className="h-9 max-w-[190px] w-auto" />
                ) : (
                  <span className="block text-[22px] lg:text-[28px] font-bold font-gilroy text-foreground tracking-[-0.02em] leading-none whitespace-nowrap truncate max-w-[180px] lg:max-w-[260px]">
                    {vendor?.name || 'Shop'}
                  </span>
                )}
              </a>
              <nav className="hidden lg:flex items-center gap-6 xl:gap-9 ml-4 flex-1 min-w-0 overflow-x-auto scrollbar-hide">
                <button
                  type="button"
                  onMouseEnter={() => { setActiveId('catalog'); setMegaOpen(true) }}
                  onClick={() => { setActiveId('catalog'); setMegaOpen(o => !o) }}
                  className={`group inline-flex items-center gap-1.5 h-9 text-[14px] font-medium shrink-0 whitespace-nowrap cursor-pointer transition-colors ${activeId === 'catalog' && megaOpen ? 'text-foreground' : 'text-foreground/65 hover:text-foreground'}`}
                >
                  <span>Дэлгүүр</span>
                  <ChevronDown className={`size-3 transition-transform ${megaOpen && activeId === 'catalog' ? 'rotate-180 opacity-100' : 'opacity-50 group-hover:opacity-90'}`} />
                </button>
                {navigation.links.filter((item: any) => !item.isHighlight).map((item: any) => (
                  <a
                    key={item.name}
                    href={navigateUrl(item.href, pathname)}
                    onMouseEnter={() => { setActiveId(item.name); setMegaOpen(false) }}
                    onClick={(e) => { e.preventDefault(); router.push(navigateUrl(item.href, pathname)) }}
                    className={`inline-flex items-center h-9 text-[14px] font-medium shrink-0 whitespace-nowrap transition-colors ${item.isSale ? 'text-red-600 hover:text-red-700' : 'text-foreground/65 hover:text-foreground'}`}
                  >
                    {item.name}
                  </a>
                ))}
              </nav>
              <div className="ml-auto flex items-center gap-1.5 lg:gap-2 shrink-0">
                <button
                  onClick={() => { setSearchOpen(o => !o); setMegaOpen(false) }}
                  aria-label="Хайх"
                  className="w-10 h-10 lg:w-11 lg:h-11 inline-flex items-center justify-center rounded-full bg-[#F1EEE7] hover:bg-[#E9E4DA] transition-colors text-foreground"
                >
                  <Search className="size-4.5" />
                </button>
                {showLogin && (
                  <a
                    href={navigateUrl(isAuthed ? '/wishlist' : '/login?url=/wishlist', pathname)}
                    aria-label="Хадгалсан"
                    className="relative w-11 h-11 hidden md:inline-flex items-center justify-center rounded-full bg-[#F1EEE7] hover:bg-[#E9E4DA] transition-colors text-foreground"
                  >
                    <Heart className="size-4.5" />
                  </a>
                )}
                {canPlaceOrder && (
                  <button
                    onClick={openCart}
                    aria-label="Сагс"
                    className="relative w-10 h-10 lg:w-11 lg:h-11 inline-flex items-center justify-center rounded-full bg-[#F1EEE7] hover:bg-[#E9E4DA] transition-colors text-foreground cursor-pointer"
                  >
                    <ShoppingBag className="size-4.5" />
                    {cartCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-foreground text-white text-[10px] font-bold inline-flex items-center justify-center tabular-nums">
                        {cartCount}
                      </span>
                    )}
                  </button>
                )}
                {showLogin && (
                  isAuthed ? (
                    <a
                      href={navigateUrl('/profile', pathname)}
                      aria-label="Профайл"
                      className="ml-1 inline-flex items-center gap-2.5 h-11 pl-1 pr-1 md:pr-4 rounded-full bg-[#F1EEE7] hover:bg-[#E9E4DA] transition-colors text-foreground"
                    >
                      {session?.user?.avatar ? (
                        <img src={formatImageURL(session.user.avatar)} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <span className="w-9 h-9 rounded-full overflow-hidden inline-flex">
                          <Avatar size={36} variant="marble" name={session?.user?.email || session?.user?.firstName || 'shop'} />
                        </span>
                      )}
                      <span className="hidden md:flex flex-col items-start leading-tight pr-0.5">
                        <span className="text-[10px] text-foreground/55">Сайн уу</span>
                        <span className="text-[13px] font-semibold truncate max-w-32">{session?.user?.firstName || 'Хэрэглэгч'}</span>
                      </span>
                    </a>
                  ) : (
                    <>
                      <a
                        href={navigateUrl('/login', pathname)}
                        aria-label="Нэвтрэх"
                        className="md:hidden w-10 h-10 inline-flex items-center justify-center rounded-full bg-foreground text-white hover:bg-foreground/90 transition-colors"
                      >
                        <User className="size-4.5" />
                      </a>
                      <a
                        href={navigateUrl('/login', pathname)}
                        className="hidden md:inline-flex ml-1 items-center gap-2.5 px-5 h-11 rounded-full bg-foreground text-white text-[14px] font-semibold hover:bg-foreground/90 transition-colors"
                      >
                        <User className="size-4" />Нэвтрэх
                      </a>
                    </>
                  )
                )}
              </div>
            </div>
            <MegaMenu open={megaOpen} onClose={() => setMegaOpen(false)} sections={navigation.catalog.sections} highlights={navigation.catalog.highlights || []} pathname={pathname} />
            <SearchPopover open={searchOpen} onClose={() => setSearchOpen(false)} pathname={pathname} isAuthed={isAuthed} />
          </div>
          <MobileDrawer open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} links={navigation.links.filter((item: any) => !item.isHighlight)} sections={navigation.catalog.sections} pathname={pathname} showLogin={showLogin} vendor={vendor} />
          <CartModal open={cartOpen} onClose={() => setCartOpen(false)} pathname={pathname} vendor={vendor} isAuthed={isAuthed} />
          {vendor?.isDanVerified === false && (
            <div className="bg-amber-50 border-t border-amber-200">
              <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 h-9 flex items-center justify-between text-[12.5px]">
                <div className="flex items-center gap-2 text-amber-900 min-w-0">
                  <ShieldAlert className="size-3.5 shrink-0" strokeWidth={2.5} />
                  <span className="truncate">
                    <span className="font-semibold">Энэ дэлгүүр баталгаажаагүй байна.</span>
                    <span className="hidden sm:inline opacity-80"> Худалдан авалт хийхдээ анхаарна уу.</span>
                  </span>
                </div>
                <a href={navigateUrl('/status', pathname)} className="inline-flex items-center gap-1 text-amber-900 font-semibold hover:underline shrink-0">
              Дэлгэрэнгүй <ChevronRight className="size-3" />
                </a>
              </div>
            </div>
          )}
        </header>
      </div>
      {/* Ghost: invisible normal-flow mirror of the fixed bar — reserves its EXACT height on the SSR
          first paint so content never sits behind the bar (no JS measurement, no hydration flash).
          Keep the row heights/breakpoints/conditions in sync with the real bar above. */}
      <div aria-hidden className="invisible pointer-events-none">
        <SubHeader vendor={vendor} />
        <div className="border-b border-foreground/5">
          <div className="h-16 lg:h-19" />
        </div>
        {vendor?.isDanVerified === false && <div className="h-9 border-t border-amber-200" />}
      </div>
    </>
  )
}

function CartModal({ open, onClose, pathname, vendor, isAuthed }: { open: boolean, onClose: () => void, pathname: string, vendor: any, isAuthed: boolean }) {
  const { cart, cartCount, initialLoading, amount, sale, orderDiscountAmount, couponDiscount, cartTotal, buyXGetYInfo, addRewardItem, availableOrderRewardItems, availableCouponRewardItems, appliedCoupon, rewardLoading, getRewardPrice, isRewardOutOfStock, getBuyXGetYRewardState, canCheckout, clearCart, freeShippingProgress, activeOrderDiscount, loyaltyDiscountAmount } = useCart()
  const cartItems: any[] = cart?.products || []
  const subtotal = amount - sale
  const discounts = (orderDiscountAmount || 0) + (couponDiscount || 0)
  const loyaltyDiscount = loyaltyDiscountAmount > 0 ? Math.min(loyaltyDiscountAmount, Math.max(0, cartTotal - (orderDiscountAmount || 0))) : 0
  const total = Math.max(0, cartTotal - (orderDiscountAmount || 0) - loyaltyDiscount)
  const hasOutOfStock = cartItems.some((p: any) => p.quantity === 0 || p.isOutOfStock)
  const allDigital = cartItems.length > 0 && cartItems.every((p: any) => p.isDigital === true)

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50 font-inter" onClose={onClose}>
        <TransitionChild as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </TransitionChild>
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <TransitionChild as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <DialogPanel className="w-full max-w-xl max-h-[88vh] bg-cream rounded-3xl shadow-2xl flex flex-col overflow-hidden">
              <header className="px-6 pt-6 pb-4 flex items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground/55 mb-1">Сагс</div>
                  <h2 className="text-[24px] font-bold font-gilroy tracking-[-0.015em] text-foreground leading-tight">
                    {cartCount > 0 ? `${cartCount} бараа · ${formatPrice(total)}` : 'Хоосон сагс'}
                  </h2>
                  {cartCount > 0 && (
                    <button onClick={clearCart} className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] font-medium text-foreground/55 hover:text-foreground transition-colors cursor-pointer">
                      <Trash2 className="size-3.5" /> Сагс хоослох
                    </button>
                  )}
                </div>
                <button onClick={onClose} aria-label="Хаах" className="w-9 h-9 rounded-full bg-foreground/5 hover:bg-foreground/10 inline-flex items-center justify-center text-foreground transition-colors cursor-pointer">
                  <X className="size-4" />
                </button>
              </header>

              {cartItems.length > 0 && (freeShippingProgress && !allDigital || (activeOrderDiscount?.type === 'order' && !appliedCoupon && activeOrderDiscount.minOrderAmount > 0)) && (
                <div className="px-6 pb-3 space-y-2.5">
                  {freeShippingProgress && !allDigital && (
                    <div className="rounded-2xl bg-[#F1EEE7] p-3">
                      <div className="flex items-center justify-between mb-1.5 text-[12.5px]">
                        <span className="inline-flex items-center gap-1.5 text-foreground font-semibold"><Truck className="size-3.5" /> Үнэгүй хүргэлт</span>
                        {freeShippingProgress.reached
                          ? <span className="text-emerald-600 font-semibold">Хангагдсан</span>
                          : <span className="text-foreground/55">{formatPrice(freeShippingProgress.remaining)} дутуу</span>}
                      </div>
                      <div className="h-1.5 rounded-full bg-foreground/10 overflow-hidden">
                        <div className="h-full bg-foreground transition-all" style={{ width: `${Math.min(100, freeShippingProgress.percentage || 0)}%` }} />
                      </div>
                    </div>
                  )}
                  {activeOrderDiscount?.type === 'order' && !appliedCoupon && activeOrderDiscount.minOrderAmount > 0 && (() => {
                    const minAmt = activeOrderDiscount.minOrderAmount
                    const target = activeOrderDiscount.targets?.find((t: any) => t.role === 'target' && t.targetId === null)
                    if (!target) return null
                    const discountLabel = target.valueType === 'percent' ? `${target.valuePercent}%-ийн хөнгөлөлт` : target.valueType === 'amount' ? `${formatPrice(target.valueAmount)} хөнгөлөлт` : null
                    if (!discountLabel) return null
                    const reached = subtotal >= minAmt
                    return (
                      <div className="rounded-2xl bg-purple-50 border border-purple-100 px-3 py-2.5">
                        <div className="flex items-start gap-2 text-[12.5px] text-purple-900">
                          <BadgePercent className="size-3.5 mt-0.5 shrink-0" />
                          <span className="font-medium">{formatPrice(minAmt)}-с дээш захиалгад {discountLabel}</span>
                        </div>
                        <div className="mt-2 flex items-center gap-3">
                          <div className="flex-1 h-1.5 rounded-full bg-purple-200 overflow-hidden">
                            <div className="h-full bg-purple-600 transition-all" style={{ width: `${Math.min((subtotal / minAmt) * 100, 100)}%` }} />
                          </div>
                          {reached ? (
                            <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-purple-700 shrink-0">
                              <Check className="size-3" /> −{formatPrice(orderDiscountAmount)}
                            </span>
                          ) : (
                            <span className="text-[11.5px] font-semibold text-purple-700 tabular-nums shrink-0">{formatPrice(minAmt - subtotal)} дутуу</span>
                          )}
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}

              <div className="flex-1 overflow-y-auto px-6">
                {initialLoading ? (
                  <div className="py-12 text-center text-[14px] text-foreground/55">Уншиж байна...</div>
                ) : cartItems.length > 0 ? (
                  <div>
                    {cartItems.map((p: any) => (
                      <CartItemSidebar key={`${p.isRewardItem ? 'reward-' : ''}${p.id}-${p.variant?.id}`} product={p} pathname={pathname} />
                    ))}

                    {buyXGetYInfo?.map((info: any) => info.qualifies && info.rewardProducts?.length > 0 && (() => {
                      const { totalRemaining } = getBuyXGetYRewardState(info, info.rewardProducts[0])
                      if (totalRemaining <= 0) return null
                      return (
                        <div key={info.discount.id} className="mt-3 rounded-2xl bg-white border border-foreground/10 p-4">
                          <div className="flex items-center gap-3">
                            <span className="w-9 h-9 rounded-full bg-[#F1EEE7] text-foreground inline-flex items-center justify-center shrink-0">
                              <Gift className="size-4" strokeWidth={2.25} />
                            </span>
                            <div className="min-w-0">
                              <div className="text-[13.5px] font-semibold text-foreground leading-tight">{info.discount?.name || 'Урамшуулал'}</div>
                              <div className="text-[11.5px] text-foreground/55 mt-0.5">
                                {getBuyXTriggerCount(info.discount)} авбал {getBuyXGetYRewardRule(info)}
                              </div>
                            </div>
                          </div>
                          <div className="mt-2 inline-flex items-center gap-1.5 text-[11.5px] text-emerald-700 font-semibold">
                            <Check className="size-3.5" /> {info.triggerCount}/{getBuyXTriggerCount(info.discount)}{totalRemaining > 0 ? ` · ${totalRemaining} бэлэг үлдсэн` : ''}
                          </div>
                          <div className="mt-2.5 space-y-2">
                            {info.rewardProducts.map((reward: any) => {
                              const { canAdd, isLockedByOther, isOutOfStock, rewardPrice } = getBuyXGetYRewardState(info, reward)
                              if (isLockedByOther) return null
                              return (
                                <div key={reward.id} className="flex items-center gap-3 p-2.5 bg-cream rounded-xl">
                                  <div className="w-11 h-11 shrink-0 rounded-lg overflow-hidden bg-white">
                                    <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full object-cover" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <div className="text-[12.5px] font-semibold text-foreground line-clamp-1">{reward.name}</div>
                                      {reward.valueType === 'free' ? (
                                        <span className="bg-red-500 text-white px-1.5 h-4 rounded-full text-[9.5px] font-bold inline-flex items-center shrink-0">Үнэгүй</span>
                                      ) : reward.valueType === 'percent' && reward.valuePercent ? (
                                        <span className="bg-red-500 text-white px-1.5 h-4 rounded-full text-[9.5px] font-bold inline-flex items-center shrink-0">-{reward.valuePercent}%</span>
                                      ) : null}
                                    </div>
                                    <div className="mt-0.5 flex items-center gap-1.5">
                                      <span className={`text-[11.5px] font-semibold tabular-nums ${rewardPrice === 0 ? 'text-emerald-600' : 'text-foreground'}`}>{rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}</span>
                                      {reward.price && rewardPrice < reward.price && <span className="text-[10.5px] text-foreground/40 line-through tabular-nums">{formatPrice(reward.price)}</span>}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    disabled={!canAdd || rewardLoading}
                                    onClick={() => addRewardItem({ ...reward, discountId: info.discount.id, discountName: info.discount.name, discountBase: info.discount.discountBase, variant: reward.variant || { id: reward.defaultVariantId, price: reward.price, priceSale: reward.priceSale, stock: reward.stock } }, 'buyXgetY')}
                                    className="h-8 px-3 rounded-full bg-foreground text-cream text-[11.5px] font-semibold inline-flex items-center gap-1 hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                                  >
                                    {rewardLoading ? <LoaderCircle className="size-3 animate-spin" /> : canAdd ? <><Plus className="size-3" /> Нэмэх</> : isOutOfStock ? 'Дууссан' : 'Нэмэгдсэн'}
                                  </button>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })())}

                    {availableCouponRewardItems?.length > 0 && (
                      <div className="my-4 rounded-2xl bg-white border border-foreground/10 p-4">
                        <div className="flex items-center gap-3">
                          <span className="w-9 h-9 rounded-full bg-[#F1EEE7] text-foreground inline-flex items-center justify-center shrink-0">
                            <Gift className="size-4" strokeWidth={2.25} />
                          </span>
                          <div className="min-w-0">
                            <div className="text-[13.5px] font-semibold text-foreground leading-tight">{appliedCoupon?.discount?.name || appliedCoupon?.name || 'Купоны бэлэг'}</div>
                            <div className="text-[11.5px] text-foreground/55 mt-0.5">Хөнгөлөлтийн кодын бэлэг сонгоно уу</div>
                          </div>
                        </div>
                        <div className="mt-2.5 space-y-2">
                          {availableCouponRewardItems.map((reward: any) => {
                            const isOutOfStock = isRewardOutOfStock(reward)
                            const rewardPrice = getRewardPrice(reward)
                            return (
                              <div key={reward.id} className="flex items-center gap-3 p-2.5 bg-cream rounded-xl">
                                <div className="w-11 h-11 shrink-0 rounded-lg overflow-hidden bg-white">
                                  <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <div className="text-[12.5px] font-semibold text-foreground line-clamp-1">{reward.name}</div>
                                    {reward.valueType === 'free' ? (
                                      <span className="bg-red-500 text-white px-1.5 h-4 rounded-full text-[9.5px] font-bold inline-flex items-center shrink-0">Үнэгүй</span>
                                    ) : reward.valueType === 'percent' && reward.valuePercent ? (
                                      <span className="bg-red-500 text-white px-1.5 h-4 rounded-full text-[9.5px] font-bold inline-flex items-center shrink-0">-{reward.valuePercent}%</span>
                                    ) : reward.valueType === 'amount' && reward.valueAmount ? (
                                      <span className="bg-red-500 text-white px-1.5 h-4 rounded-full text-[9.5px] font-bold inline-flex items-center shrink-0">-{formatPrice(reward.valueAmount)}</span>
                                    ) : null}
                                  </div>
                                  <div className="mt-0.5 flex items-center gap-1.5">
                                    <span className={`text-[11.5px] font-semibold tabular-nums ${rewardPrice === 0 ? 'text-emerald-600' : 'text-foreground'}`}>{rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}</span>
                                    {reward.price && rewardPrice < reward.price && <span className="text-[10.5px] text-foreground/40 line-through tabular-nums">{formatPrice(reward.price)}</span>}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  disabled={isOutOfStock || rewardLoading}
                                  onClick={() => addRewardItem(reward, 'order')}
                                  className="h-8 px-3 rounded-full bg-foreground text-cream text-[11.5px] font-semibold inline-flex items-center gap-1 hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                                >
                                  {rewardLoading ? <LoaderCircle className="size-3 animate-spin" /> : isOutOfStock ? 'Дууссан' : <><Plus className="size-3" /> Нэмэх</>}
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {availableOrderRewardItems?.length > 0 && (
                      <div className="my-4 rounded-2xl bg-white border border-foreground/10 p-4">
                        <div className="flex items-center gap-3">
                          <span className="w-9 h-9 rounded-full bg-[#F1EEE7] text-foreground inline-flex items-center justify-center shrink-0">
                            <Gift className="size-4" strokeWidth={2.25} />
                          </span>
                          <div className="min-w-0">
                            <div className="text-[13.5px] font-semibold text-foreground leading-tight">Захиалгын бэлэг</div>
                            <div className="text-[11.5px] text-foreground/55 mt-0.5">Захиалгын урамшууллын бэлэг сонгоно уу</div>
                          </div>
                        </div>
                        <div className="mt-2.5 space-y-2">
                          {availableOrderRewardItems.map((reward: any) => {
                            const isOutOfStock = isRewardOutOfStock(reward)
                            const rewardPrice = getRewardPrice(reward)
                            return (
                              <div key={reward.id} className="flex items-center gap-3 p-2.5 bg-cream rounded-xl">
                                <div className="w-11 h-11 shrink-0 rounded-lg overflow-hidden bg-white">
                                  <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <div className="text-[12.5px] font-semibold text-foreground line-clamp-1">{reward.name}</div>
                                    {reward.valueType === 'free' ? (
                                      <span className="bg-red-500 text-white px-1.5 h-4 rounded-full text-[9.5px] font-bold inline-flex items-center shrink-0">Үнэгүй</span>
                                    ) : reward.valueType === 'percent' && reward.valuePercent ? (
                                      <span className="bg-red-500 text-white px-1.5 h-4 rounded-full text-[9.5px] font-bold inline-flex items-center shrink-0">-{reward.valuePercent}%</span>
                                    ) : null}
                                  </div>
                                  <div className="mt-0.5 flex items-center gap-1.5">
                                    <span className={`text-[11.5px] font-semibold tabular-nums ${rewardPrice === 0 ? 'text-emerald-600' : 'text-foreground'}`}>{rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}</span>
                                    {reward.price && rewardPrice < reward.price && <span className="text-[10.5px] text-foreground/40 line-through tabular-nums">{formatPrice(reward.price)}</span>}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  disabled={isOutOfStock || rewardLoading}
                                  onClick={() => addRewardItem(reward, 'order')}
                                  className="h-8 px-3 rounded-full bg-foreground text-cream text-[11.5px] font-semibold inline-flex items-center gap-1 hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                                >
                                  {rewardLoading ? <LoaderCircle className="size-3 animate-spin" /> : isOutOfStock ? 'Дууссан' : <><Plus className="size-3" /> Нэмэх</>}
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="py-16 flex flex-col items-center text-center">
                    <div className="w-20 h-20 rounded-3xl bg-[#F1EEE7] inline-flex items-center justify-center mb-4">
                      <ShoppingBag className="size-8 text-foreground/40" />
                    </div>
                    <div className="text-[16px] font-semibold text-foreground">Таны сагс хоосон байна</div>
                    <div className="mt-1.5 text-[13px] text-foreground/55 max-w-xs">Дэлгүүрээс таалагдсан бараагаа сонгоод, сагсандаа нэмээрэй.</div>
                    <a
                      href={navigateUrl('/product', pathname)}
                      onClick={onClose}
                      className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-foreground text-cream text-[14px] font-semibold hover:bg-foreground/90 transition-colors"
                    >
                      <ShoppingBag className="size-4" /> Дэлгүүр хэсэх
                    </a>
                  </div>
                )}
              </div>

              {cartItems.length > 0 && (() => {
                const belowMin = vendor?.orderMinAmount > 0 && subtotal < vendor.orderMinAmount
                return (
                  <footer className="border-t border-foreground/10 bg-cream px-6 py-5">
                    {hasOutOfStock && (
                      <div className="mb-3 rounded-2xl bg-red-50 border border-red-100 px-4 py-3 flex items-start gap-3">
                        <PackageX className="size-4 text-red-500 shrink-0 mt-0.5" />
                        <div className="text-[12.5px] leading-snug">
                          <div className="font-semibold text-red-600">Үлдэгдэл дууссан бараа</div>
                          <div className="text-red-600/85 mt-0.5">Таны сагсанд үлдэгдэл хүрэлцэхгүй бараа байна, шалгаад үргэлжлүүлнэ үү.</div>
                        </div>
                      </div>
                    )}
                    {belowMin && (
                      <div className="mb-4 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-3">
                        <AlertTriangle className="size-4 text-amber-600 shrink-0 mt-0.5" />
                        <div className="text-[12.5px] leading-snug">
                          <div className="font-semibold text-amber-800">Захиалгын доод дүнд хүрээгүй байна</div>
                          <div className="text-amber-700 mt-0.5">Хамгийн багадаа <span className="font-semibold tabular-nums">{formatPrice(vendor.orderMinAmount)}</span>-ийн захиалга өгөх ёстой. <span className="font-semibold tabular-nums">{formatPrice(vendor.orderMinAmount - subtotal)}</span> дутуу.</div>
                          <div className="mt-2 h-1.5 rounded-full bg-amber-200 overflow-hidden">
                            <div className="h-full bg-amber-500 transition-all" style={{ width: `${Math.min(100, (subtotal / vendor.orderMinAmount) * 100)}%` }} />
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="space-y-1.5 text-[13px] mb-4">
                      <div className="flex items-center justify-between text-foreground/65">
                        <span>Барааны үнэ</span>
                        <span className="text-foreground font-medium">{formatPrice(subtotal)}</span>
                      </div>
                      {discounts > 0 && (
                        <div className="flex items-center justify-between text-foreground/65">
                          <span>Хямдрал</span>
                          <span className="text-emerald-600 font-medium">−{formatPrice(discounts)}</span>
                        </div>
                      )}
                      {loyaltyDiscount > 0 && (
                        <div className="flex items-center justify-between text-foreground/65">
                          <span>Лоялти хөнгөлөлт</span>
                          <span className="text-violet-600 font-medium">−{formatPrice(loyaltyDiscount)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-baseline justify-between mb-4">
                      <span className="text-[14px] font-semibold text-foreground">Нийт</span>
                      <span className="text-[26px] font-bold font-gilroy tracking-[-0.015em] text-foreground">{formatPrice(total)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5">
                      <a
                        href={navigateUrl('/cart', pathname)}
                        onClick={onClose}
                        className="h-12 rounded-full bg-white border border-foreground/15 hover:border-foreground/40 transition-colors text-[14px] font-semibold text-foreground inline-flex items-center justify-center"
                      >
                        Сагс үзэх
                      </a>
                      {belowMin || !canCheckout() ? (
                        <button
                          type="button"
                          disabled
                          className="h-12 rounded-full bg-foreground/40 text-cream/80 text-[14px] font-semibold inline-flex items-center justify-center gap-2 cursor-not-allowed"
                        >
                          Худалдан авах <ArrowRight className="size-4" />
                        </button>
                      ) : (
                        <a
                          href={navigateUrl(getCheckoutPath(vendor, isAuthed), pathname)}
                          onClick={onClose}
                          className="h-12 rounded-full bg-foreground hover:bg-foreground/90 transition-colors text-[14px] font-semibold text-cream inline-flex items-center justify-center gap-2"
                        >
                          Худалдан авах <ArrowRight className="size-4" />
                        </a>
                      )}
                    </div>
                  </footer>
                )
              })()}
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  )
}
